import { type ISODate, PeriodType, toISODate } from "@inrev/common";
import { Option } from "@inrev/core";
import { hasPendingFinancialExtraction } from "@portal/api/financials";
import { DateTime } from "luxon";
import type { ComputeActionItemsInput } from "./computeActionItems";
import {
	getLatestFyePeriod,
	getYtdPeriod,
	isFyePeriodExpired,
	latestFyeMissing,
	nextRequiredFyeDate,
	yearPriorFyeMissing,
} from "./financialPeriods";
import {
	hasEmptyIndividuals,
	hasPartialHistory,
	hasPartialIndividuals,
	hasPartialWip,
	isWipMissing,
} from "./partialFields";
import type { ActionItem, ProfitabilityField } from "./types";

const defaultLatestFyeDate = (): ISODate =>
	toISODate(DateTime.now().startOf("year").minus({ days: 1 }));

function nextRequiredOrDefaultFyeDate(periodEndDate: ISODate | undefined): ISODate {
	if (!periodEndDate) return defaultLatestFyeDate();
	if (isFyePeriodExpired(periodEndDate)) return nextRequiredFyeDate(periodEndDate);
	return periodEndDate;
}

const missingFyeStatementLabel = (periodEndDate: ISODate): string =>
	`${DateTime.fromISO(periodEndDate).toFormat("MM/dd/yyyy")} FYE Financial Statement`;

const missingFyeStatementReason = (periodEndDate: ISODate): string =>
	`Please provide a ${DateTime.fromISO(periodEndDate).year} FYE financial statement.`;

const isProfitabilityMissing = (period: { profitable?: boolean } | undefined): boolean =>
	!period || Option.isNone(period.profitable);

function computeFinancialItems(input: ComputeActionItemsInput): ActionItem[] {
	const { account, financialPeriods, financialExtractions } = input;
	const { modelSize } = account;
	const items: ActionItem[] = [];

	if (modelSize == null) {
		return [];
	}

	const isStatementInFlight = hasPendingFinancialExtraction(financialExtractions);

	const ytdPeriod = getYtdPeriod(financialPeriods);
	const latestFye = getLatestFyePeriod(financialPeriods);

	if (modelSize === "small") {
		const fields: ProfitabilityField[] = [];
		if (isProfitabilityMissing(ytdPeriod)) fields.push("profitableYtd");
		if (isProfitabilityMissing(latestFye)) fields.push("profitableLastFye");
		if (fields.length > 0) {
			items.push({
				key: "financials-profitability",
				severity: "warn",
				label: "Financials",
				reason: "Profitability details are required for this account.",
				action: { type: "profitability", fields },
				actionLabel: "Add profitability",
			});
		}
		return items;
	}

	if (isProfitabilityMissing(ytdPeriod)) {
		items.push({
			key: "financials-profitability",
			severity: "warn",
			label: "Financials",
			reason: "Profitability details are required for this account.",
			action: { type: "profitability", fields: ["profitableYtd"] },
			actionLabel: "Add profitability",
		});
	}

	if (modelSize === "large") {
		if (!latestFye || latestFye.blocSize == null) {
			const fyeDate = latestFye?.periodEndDate ?? defaultLatestFyeDate();
			items.push({
				key: "financials-bloc-size",
				severity: "warn",
				label: "Financials",
				reason: "Bank Line of Credit (BLOC) size is required for the latest fiscal year end.",
				action: { type: "blocSize", fyeDate },
				actionLabel: "Set BLOC size",
			});
		}
	}

	if (!isStatementInFlight && latestFyeMissing(financialPeriods, financialExtractions)) {
		const periodEndDate = nextRequiredOrDefaultFyeDate(latestFye?.periodEndDate);
		items.push({
			key: "missing-financials",
			severity: "warn",
			label: missingFyeStatementLabel(periodEndDate),
			reason: missingFyeStatementReason(periodEndDate),
			action: { type: "upload", periodEndDate, periodType: PeriodType.Fye },
			actionLabel: "Upload statement",
		});
	}

	if (modelSize === "large" && !isStatementInFlight) {
		const yearPriorResult = yearPriorFyeMissing(financialPeriods, financialExtractions);
		if (yearPriorResult.missing) {
			const periodEndDate = yearPriorResult.periodEndDate;
			items.push({
				key: "missing-financials-year-prior",
				severity: "warn",
				label: missingFyeStatementLabel(periodEndDate),
				reason: missingFyeStatementReason(periodEndDate),
				action: {
					type: "upload",
					periodEndDate,
					periodType: PeriodType.Fye,
				},
				actionLabel: "Upload statement",
			});
		}
	}

	return items;
}

export function computeMissingInfoItems(input: ComputeActionItemsInput): ActionItem[] {
	const { account, individuals, latestWipStatement } = input;
	const items: ActionItem[] = [];

	items.push(...computeFinancialItems(input));

	if (hasEmptyIndividuals(individuals)) {
		items.push({
			key: "missing-individuals",
			severity: "warn",
			label: "Individuals",
			reason: "Missing information",
			action: { type: "navigate", navigate: { tabId: "details", subsectionId: "individuals" } },
			actionLabel: "View section",
		});
	} else if (hasPartialIndividuals(individuals)) {
		items.push({
			key: "partial-individuals",
			severity: "warn",
			label: "Individuals",
			reason: "Missing information",
			action: { type: "navigate", navigate: { tabId: "details", subsectionId: "individuals" } },
			actionLabel: "View section",
		});
	}

	if (hasPartialHistory(account, input.showContractHistory ?? true)) {
		items.push({
			key: "partial-history",
			severity: "warn",
			label: "History",
			reason: "Missing information",
			action: { type: "navigate", navigate: { tabId: "details", subsectionId: "history" } },
			actionLabel: "View section",
		});
	}

	if (isWipMissing(latestWipStatement, account.modelSize)) {
		items.push({
			key: "missing-wip",
			severity: "warn",
			label: "WIP",
			reason: "Missing information",
			action: { type: "navigate", navigate: { tabId: "wip" } },
			actionLabel: "View section",
		});
	} else if (hasPartialWip(latestWipStatement, account.modelSize)) {
		items.push({
			key: "partial-wip",
			severity: "warn",
			label: "WIP",
			reason: "Missing information",
			action: { type: "navigate", navigate: { tabId: "wip" } },
			actionLabel: "View section",
		});
	}

	return items;
}
